class SectionVideo extends HTMLElement {
  constructor() {
    super();
    this.played = false;
  }

  connectedCallback() {
    this.autoplay = this.dataset.autoplay === 'true';
    this.lazyLoad = this.dataset.lazyLoad === 'true';
    this.hasThumbnail = this.dataset.hasThumbnail === 'true';

    this.video = this.querySelector('video');
    this.iframe = this.querySelector('[data-youtube-iframe]');
    this.thumbnail = this.querySelector('[data-thumbnail]');
    this.playButton = this.querySelector('[data-play-button]');
    this.container = this.querySelector('.section-video__container');

    if (this.hasThumbnail && this.container) {
      this.container.addEventListener('click', () => this.play());
    }

    if (this.video) {
      this.video.addEventListener('loadedmetadata', () => this.adjustAspectRatio());
      this.video.addEventListener('playing', () => this.hideOverlay(), { once: true });
    }

    if (this.iframe && this.autoplay) {
      this.iframe.addEventListener('load', () => this.hideOverlay());
    }

    const needsViewportTrigger =
      (this.iframe && this.lazyLoad && this.iframe.dataset.src) ||
      (this.video && this.autoplay && this.lazyLoad);

    if (needsViewportTrigger) {
      this.observeViewport();
    } else if (this.video && this.autoplay) {
      this.play();
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  observeViewport() {
    if (!('IntersectionObserver' in window)) {
      this.onViewportEnter();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.onViewportEnter();
            this.observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.25 }
    );
    this.observer.observe(this);
  }

  onViewportEnter() {
    if (this.iframe && this.iframe.dataset.src && !this.iframe.src) {
      this.iframe.src = this.iframe.dataset.src;
    }
    if (this.video && this.autoplay) {
      this.play();
    }
  }

  play() {
    if (this.played) return;
    this.played = true;

    this.hideOverlay();

    if (this.video) {
      this.video.muted = true;
      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    if (this.iframe && !this.autoplay) {
      if (!this.iframe.src && this.iframe.dataset.src) {
        this.iframe.src = this.iframe.dataset.src;
      }
      if (this.iframe.contentWindow) {
        this.iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
          'https://www.youtube.com'
        );
      }
    }
  }

  hideOverlay() {
    const thumbnail = this.thumbnail || this.querySelector('[data-thumbnail]');
    const playButton = this.playButton || this.querySelector('[data-play-button]');
    if (thumbnail) {
      thumbnail.classList.add('is-hidden');
      thumbnail.style.opacity = '0';
      thumbnail.style.pointerEvents = 'none';
    }
    if (playButton) {
      playButton.classList.add('is-hidden');
      playButton.style.opacity = '0';
      playButton.style.pointerEvents = 'none';
    }
  }

  adjustAspectRatio() {
    if (!this.container || !this.video || !this.video.videoWidth || !this.video.videoHeight) return;
    const ratio = (this.video.videoHeight / this.video.videoWidth) * 100;
    this.container.style.paddingBottom = ratio + '%';
  }
}

if (!customElements.get('section-video')) {
  customElements.define('section-video', SectionVideo);
}
